import React__default from "react";
import { useFloating, flip, shift, size, offset, autoUpdate, useRole, useDismiss, useListNavigation, useInteractions, FloatingPortal, FloatingFocusManager } from "@floating-ui/react";
import { AnalyticsTrigger } from "../../helpers/analytics/analytics-trigger/analytics-trigger.mjs";
import { HeaderContext } from "../../helpers/header-provider/header-provider.mjs";
import { useLabels } from "../../helpers/label-provider/use-labels.mjs";
import { isSuggestionGroup, Suggestion } from "../../suggestion/suggestion.mjs";
import { TextField } from "../textfield/textfield.mjs";
import { useTheme } from "../../helpers/theme-provider/use-theme.mjs";
const createItemProps = ({
  item,
  index,
  listRef,
  activeIndex,
  getItemProps,
  onSelectItem,
  propsId
}) => {
  return {
    ...item,
    ...getItemProps({
      ref(node) {
        listRef.current[index] = node;
      },
      onClick(event) {
        var _a;
        event.preventDefault();
        (_a = item.onClick) == null ? void 0 : _a.call(item, event);
        onSelectItem(item);
      },
      id: "".concat(propsId, "-suggestion-item-").concat(index),
      key: "".concat(propsId, "-suggestion-item-").concat(index),
      role: "option",
      "aria-selected": activeIndex === index ? "true" : "false"
    }),
    isFocused: activeIndex === index,
    tabIndex: -1
  };
};
const mapSubItems = ({
  subItems,
  listRef,
  activeIndex,
  getItemProps,
  onSelectItem,
  propsId,
  globalIndex
}) => {
  return subItems.map((subItem) => {
    const itemProps = createItemProps({
      item: subItem,
      index: globalIndex.current,
      listRef,
      activeIndex,
      getItemProps,
      onSelectItem,
      propsId
    });
    globalIndex.current++;
    return itemProps;
  });
};
const mapSuggestionItems = ({
  items,
  activeIndex,
  listRef,
  getItemProps,
  onSelectItem,
  propsId
}) => {
  const globalIndex = { current: 0 };
  return items.map((item) => {
    if (isSuggestionGroup(item)) {
      return {
        ...item,
        items: mapSubItems({
          subItems: item.items,
          listRef,
          activeIndex,
          getItemProps,
          onSelectItem,
          propsId,
          globalIndex
        })
      };
    }
    const itemProps = createItemProps({
      item,
      index: globalIndex.current,
      listRef,
      activeIndex,
      getItemProps,
      onSelectItem,
      propsId
    });
    globalIndex.current++;
    return itemProps;
  });
};
const SuggestionField = (props) => {
  const labels = useLabels();
  const { portalRoot } = useTheme();
  return /* @__PURE__ */ React__default.createElement(
    AnalyticsTrigger,
    {
      trigger: [
        {
          callback: "onSelect",
          id: "Suggestion - Choose - ".concat(props.id),
          elementId: "".concat(props.id),
          elementType: "suggestion",
          interactionType: "select"
        },
        {
          callback: "onShow",
          id: "Suggestion - Open - ".concat(props.id),
          elementId: "".concat(props.id),
          elementType: "suggestion",
          interactionType: "open"
        }
      ]
    },
    /* @__PURE__ */ React__default.createElement(SuggestionFieldInner, { ...props, labels, portalContainer: props.portalContainer || portalRoot })
  );
};
const SuggestionFieldInner = (props) => {
  const {
    contentRenderer,
    defaultValue,
    fullWidth,
    id,
    inputRef,
    isDisabled,
    isLoading,
    isReadOnly,
    items = [],
    labels,
    offsetY = -1,
    onBlur: handleBlur,
    onChange: handleChange,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    onScrollToEnd,
    onSelect,
    onShow,
    portalContainer,
    showSuggestionsOnFocus,
    suggestionClassName,
    suggestionContent,
    value: controlledValue,
    minValueLength = 1,
    ...rest
  } = props;
  const [stateValue, setStateValue] = React__default.useState(defaultValue != null ? defaultValue : "");
  const [isVisible, setIsVisible] = React__default.useState(false);
  const [activeIndex, setActiveIndex] = React__default.useState(null);
  const listRef = React__default.useRef([]);
  const suggestionListRef = React__default.useRef(null);
  const isSafari = React__default.useMemo(() => {
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes("safari") && !ua.includes("chrome") && !ua.includes("android") && ua.includes("applewebkit");
  }, []);
  const handleOpenChange = (newVisible) => {
    if (value.length >= minValueLength || !newVisible) {
      setVisibility(newVisible);
    }
  };
  const setVisibility = (newVisible) => {
    setIsVisible(newVisible);
    if (newVisible && onShow) {
      onShow();
    }
  };
  const { headerHeight, isHeaderHidden, headerBottomHeight } = React__default.useContext(HeaderContext);
  const padding = {
    top: isHeaderHidden ? (
      // employee header is always visible
      headerHeight - headerBottomHeight + 8
    ) : headerHeight + 8,
    right: 0,
    bottom: 8,
    left: 0
  };
  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
    middleware: [
      flip({ padding }),
      shift(),
      size({
        padding,
        apply({ rects, elements, availableHeight, availableWidth }) {
          Object.assign(elements.floating.style, {
            maxHeight: "".concat(Math.max(100, availableHeight), "px")
          });
          if (!fullWidth) {
            Object.assign(elements.floating.style, {
              width: "".concat(rects.reference.width, "px")
            });
          } else {
            Object.assign(elements.floating.style, {
              minWidth: "".concat(rects.reference.width, "px"),
              maxWidth: "".concat(availableWidth, "px")
            });
          }
        }
      }),
      offset({
        mainAxis: offsetY
      })
    ],
    whileElementsMounted: autoUpdate,
    open: isVisible,
    onOpenChange: handleOpenChange
  });
  const setSuggestionListRef = React__default.useCallback(
    (node) => {
      suggestionListRef.current = node;
      refs.setFloating(node);
    },
    [refs]
  );
  const role = useRole(context, { role: "listbox" });
  const dismiss = useDismiss(context);
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    focusItemOnHover: false,
    virtual: true,
    loop: true
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([role, dismiss, listNav]);
  const isValueControlled = React__default.useCallback(
    (ctrlValue = controlledValue) => {
      const isDefined = typeof ctrlValue !== "undefined";
      if (isReadOnly || isDisabled) {
        return isDefined;
      }
      return !!handleChange && isDefined;
    },
    [controlledValue, handleChange, isReadOnly, isDisabled]
  );
  const value = isValueControlled(controlledValue) ? controlledValue : stateValue;
  const getZIndex = React__default.useCallback(() => {
    if (refs.domReference.current) {
      const inHeader = refs.domReference.current.closest(".header") !== null;
      const inModal = refs.domReference.current.closest(".modal") !== null;
      if (inHeader || inModal) {
        return 30;
      }
    }
    return 19;
  }, [refs.domReference]);
  const onChange = (newValue) => {
    if (!isValueControlled()) {
      setStateValue(newValue);
    }
    if (handleChange) {
      handleChange(newValue);
    }
    if (newValue.length >= minValueLength || showSuggestionsOnFocus) {
      setVisibility(true);
    } else {
      setVisibility(false);
    }
    setActiveIndex(null);
  };
  const onSelectItem = (selectedItem) => {
    var _a;
    onChange(selectedItem.label);
    onSelect == null ? void 0 : onSelect(selectedItem);
    (_a = refs.domReference.current) == null ? void 0 : _a.focus();
    setVisibility(false);
  };
  const onScroll = () => {
    const elem = suggestionListRef.current;
    if (onScrollToEnd && elem && elem.scrollHeight - elem.scrollTop <= elem.offsetHeight) {
      onScrollToEnd();
    }
  };
  const onFocus = () => {
    if ((value.length >= minValueLength || showSuggestionsOnFocus) && !isVisible) {
      setVisibility(true);
    }
    if (handleFocus) {
      handleFocus();
    }
  };
  const onBlur = (event) => {
    if (handleBlur) {
      handleBlur(event);
    }
  };
  const handleEnter = (event) => {
    if (activeIndex !== null && isVisible) {
      event.preventDefault();
      const flattenedItems = flattenItems();
      const currentItem = flattenedItems[activeIndex];
      if (currentItem) {
        onSelectItem(currentItem);
      }
    } else if (isVisible) {
      setVisibility(false);
    }
  };
  const handleResetActiveIndex = (event) => {
    if (activeIndex !== null && isVisible) {
      event.preventDefault();
      setActiveIndex(null);
    }
  };
  const onKeyDown = (event) => {
    switch (event.key) {
      case "Enter":
        handleEnter(event);
        break;
      case "Home":
      case "End":
        handleResetActiveIndex(event);
        break;
      case "ArrowLeft":
      case "ArrowRight":
        if (isVisible) {
          setActiveIndex(null);
        }
        break;
    }
    if (handleKeyDown) {
      handleKeyDown(event);
    }
  };
  const flattenItems = React__default.useCallback(() => {
    const flatItems = [];
    items.forEach((item) => {
      if (isSuggestionGroup(item)) {
        flatItems.push(...item.items);
      } else {
        flatItems.push(item);
      }
    });
    return flatItems;
  }, [items]);
  const activeOptionLabel = React__default.useMemo(() => {
    if (activeIndex === null || items.length === 0) {
      return "";
    }
    const flattenedItems = flattenItems();
    const activeItem = flattenedItems[activeIndex];
    return activeItem ? activeItem.label : "";
  }, [activeIndex, items.length, flattenItems]);
  const getAssistiveText = (count) => {
    if (isLoading) {
      return labels.get("loading");
    }
    switch (count) {
      case 0:
        return labels.get("suggestionField.noResultsFound");
      case 1:
        return labels.get("suggestionField.oneResultFound");
      default:
        return labels.get("suggestionField.resultsFound", {
          count: count.toString()
        });
    }
  };
  const renderAssistiveText = () => {
    return /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, /* @__PURE__ */ React__default.createElement("span", { "aria-live": "polite", role: "status", className: "sr-only suggestionfield__assistive-text" }, isVisible && getAssistiveText(items.length)), isSafari && /* @__PURE__ */ React__default.createElement("span", { "aria-live": "polite", role: "status", className: "sr-only suggestionfield__assistive-text" }, isVisible && activeOptionLabel));
  };
  const combinedContentRenderer = () => {
    const assistiveText = renderAssistiveText();
    const previousContent = contentRenderer ? contentRenderer() : null;
    return /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, assistiveText, previousContent);
  };
  const noResultsMessage = isVisible && !items.length && !isLoading ? labels.get("suggestionField.noResultsFound") : void 0;
  const renderSuggestion = () => {
    if (!isVisible) {
      return null;
    }
    const mappedItems = mapSuggestionItems({
      items,
      activeIndex,
      listRef,
      getItemProps,
      onSelectItem,
      propsId: id
    });
    const floatingProps = getFloatingProps({
      ref: null,
      style: {
        ...floatingStyles,
        zIndex: getZIndex()
      }
    });
    return /* @__PURE__ */ React__default.createElement(FloatingPortal, { root: portalContainer }, /* @__PURE__ */ React__default.createElement(FloatingFocusManager, { context, initialFocus: -1 }, /* @__PURE__ */ React__default.createElement(
      Suggestion,
      {
        ...floatingProps,
        id: "".concat(id, "-suggestion"),
        className: suggestionClassName,
        items: mappedItems,
        highlightedValue: value,
        isLoading,
        noResultsMessage,
        additionalContent: suggestionContent,
        onScroll,
        listRef: setSuggestionListRef,
        "aria-labelledby": "".concat(id, "-label")
      }
    )));
  };
  return /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, /* @__PURE__ */ React__default.createElement(
    TextField,
    {
      ...rest,
      id,
      isReadOnly,
      isDisabled,
      value,
      onChange,
      inputRef: (el) => {
        refs.setReference(el);
        if (inputRef) {
          inputRef(el);
        }
      },
      contentRenderer: combinedContentRenderer,
      input: {
        autoComplete: "off",
        "aria-autocomplete": "list",
        "aria-controls": isVisible ? "".concat(id, "-suggestion") : void 0,
        "aria-activedescendant": activeIndex !== null ? "".concat(id, "-suggestion-item-").concat(activeIndex) : void 0,
        role: "combobox",
        "aria-expanded": isVisible ? "true" : "false",
        "aria-owns": isVisible ? "".concat(id, "-suggestion") : void 0,
        "aria-haspopup": "listbox"
      },
      ...getReferenceProps({
        onFocus,
        onBlur,
        onKeyDown
      })
    }
  ), renderSuggestion());
};
export {
  SuggestionField
};
